import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { EstoqarMark, EstoqarWordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { BACKEND_URL } from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [serverUp, setServerUp] = useState(null);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/health`)
      .then((r) => setServerUp(r.ok))
      .catch(() => setServerUp(false));
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Falha ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row" data-testid="login-page">
      <div className="relative flex-1 min-h-[42vh] lg:min-h-screen overflow-hidden">
        <img src="/img/warehouse.jpg" alt="Estoque com prateleiras e caixas" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-white/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-8 lg:p-14 max-w-2xl">
          <div className="flex items-center gap-3 mb-8">
            <EstoqarMark size={44} boxColor="#0B1F17" boxStroke="#2DC26B" />
            <div>
              <EstoqarWordmark light={false} size="text-2xl" />
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#0F6B3A]">Gestão de operações logísticas</div>
            </div>
          </div>
          <div className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#0F6B3A] mb-3">Centro de Distribuição</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0F2A1F] leading-[1.05]">Controle de Operações</h1>
          <p className="text-sm sm:text-base text-[#5A6B62] mt-5 max-w-lg leading-relaxed">Gestão, performance e eficiência operacional para acompanhar resultados, identificar oportunidades e garantir uma operação logística mais ágil e eficiente.</p>
        </div>
      </div>

      <div className="w-full lg:w-[440px] xl:w-[480px] bg-white border-l border-[#D9E3DC] flex items-center" data-testid="login-form">
        <form onSubmit={submit} className="w-full px-8 sm:px-12 py-10 space-y-5">
          <EstoqarMark size={44} boxColor="#0B1F17" boxStroke="#2DC26B" />
          <div>
            <h2 className="text-2xl font-extrabold text-[#0F2A1F] tracking-tight">Acessar painel</h2>
            <p className="text-sm text-[#5A6B62] mt-1">Entre com suas credenciais do ESTOQAR</p>
          </div>
          <div>
            <label className="text-[11px] font-mono font-semibold text-[#0F2A1F] uppercase tracking-[0.2em]">E-mail</label>
            <input data-testid="login-input-username" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu.email@gmail.com.br"
              className="mt-1.5 w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-3 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#2DC26B] placeholder:text-[#8A9A90]" />
          </div>
          <div>
            <label className="text-[11px] font-mono font-semibold text-[#0F2A1F] uppercase tracking-[0.2em]">Senha</label>
            <input data-testid="login-input-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="mt-1.5 w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-3 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#2DC26B] placeholder:text-[#8A9A90]" />
          </div>
          {error && (
            <div data-testid="login-error-message" className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</div>
          )}
          <button data-testid="login-submit-button" type="submit" disabled={loading}
            className="w-full bg-[#2DC26B] hover:bg-[#22A85B] disabled:opacity-60 text-white font-bold px-4 py-3.5 rounded-md shadow-sm flex items-center justify-center gap-2 transition-colors">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Entrar
          </button>
          <div className="flex items-center gap-2 text-xs text-[#5A6B62] pt-2" data-testid="server-status-indicator">
            <span className={`w-2 h-2 rounded-full ${serverUp === null ? "bg-[#8A9A90]" : serverUp ? "bg-[#2DC26B]" : "bg-[#EF4444]"}`} />
            Servidor {serverUp === null ? "verificando..." : serverUp ? "online" : "indisponível"}
          </div>
        </form>
      </div>
    </div>
  );
}
